import type { Theme } from "@mui/material";
import {
  Box,
  Dialog,
  DialogContent,
  Divider,
  FormControl,
  FormHelperText,
  FormLabel,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { DIALOG_WIDTH } from "../../constants";
import StandardDialogTitle from "./StandardDialogTitle";
import { useCreateDashboard } from "../../hooks/mutation/create/useCreateDashboard";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Schemas } from "../../schemas";
import StandardDialogActions from "./StandardDialogActions";
import z from "zod";
import * as React from "react";
import { CloseDialogHandler } from "../../contexts/DialogContext";

const schema = Schemas.dashboard.omit({
  id: true,
  metrics: true,
});

const NewDashboardDialog: React.FC<{
  onDialogClosed: CloseDialogHandler;
}> = ({ onDialogClosed }) => {
  const isSmallScreen = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("sm"),
  );
  const { mutateAsync: createDashboard, isPending: isCreating } =
    useCreateDashboard();

  const [submitError, setSubmitError] = React.useState<string | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    defaultValues: {},
    mode: "onBlur",
  });

  const onSubmit = async (data: z.infer<typeof schema>) => {
    try {
      setSubmitError(null);
      await createDashboard(data);
      reset({ name: "", description: "" });
      onDialogClosed({
        message: "Dashboard created!",
        severity: "success",
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Something went wrong.";
      setSubmitError(msg);
    }
  };

  const onClose = () => {
    setSubmitError(null);
    reset({ name: "", description: "" });
    onDialogClosed();
  };

  return (
    <Dialog
      open={true}
      onClose={onClose}
      fullWidth
      fullScreen={isSmallScreen}
      PaperProps={{
        sx: { maxWidth: isSmallScreen ? undefined : DIALOG_WIDTH.lg },
      }}
    >
      <StandardDialogTitle
        title={"New Control Plane"}
        isSmallScreen={isSmallScreen}
        onClose={onClose}
      />

      <Box
        component="form"
        id="new-dashboard-form"
        onSubmit={handleSubmit(onSubmit)}
      >
        <DialogContent sx={{ overflowX: "hidden" }}>
          <Box sx={{ width: "100%" }}>
            <Stack sx={{ mb: 1.5 }}>
              <Typography variant="body2" color="text.secondary">
              Create a Control Plane and optionally declare its dependencies.
              </Typography>
            </Stack>

            <Divider sx={{ mb: 1.5 }} />

            <Stack spacing={2.25} sx={{ width: "100%" }}>
              {submitError && (
                <FormHelperText error sx={{ fontSize: 13, m: 0 }}>
                  {submitError}
                </FormHelperText>
              )}

              <FormControl>
                <FormLabel>Name</FormLabel>
                <TextField
                  size="small"
                  placeholder="e.g. Payments Service"
                  error={!!errors.name}
                  {...register("name", {
                    onChange: () => submitError && setSubmitError(null),
                  })}
                />
                <FormHelperText error={!!errors.name}>
                  {errors.name?.message ?? " "}
                </FormHelperText>
              </FormControl>

              <FormControl>
                <FormLabel>Description</FormLabel>
                <TextField
                  size="small"
                  multiline
                  minRows={2}
                  placeholder="Short description of the system"
                  error={!!errors.description}
                  {...register("description", {
                    onChange: () => submitError && setSubmitError(null),
                  })}
                />
                <FormHelperText error={!!errors.description}>
                  {errors.description?.message ?? " "}
                </FormHelperText>
              </FormControl>
            </Stack>
          </Box>
        </DialogContent>

        <StandardDialogActions
          primaryText={"Create"}
          primaryIcon={<></>}
          onCancel={onClose}
          loading={isCreating}
          disabled={isCreating}
        />
      </Box>
    </Dialog>
  );
};

export default NewDashboardDialog;
